import { FunctionComponent, useState } from 'react'
import styles from './planning.module.scss'
import { Daycare } from '@/types/Daycare'
import { ClosingDay } from '@/types/ClosingDays'
import { Work } from '@/types/work'
import { OccasionalSlotsPlaces } from '@/types/Slots'
import PlanningKPI from './KPI/planningKPI'
import EditOrCreateWorkDialog from './Dialog/staffWork/editOrCreateStaffWork/editOrCreateWorkDialog'
import { getDaycareClosing, getDaycareOpening } from '@/modules/helper/daycareHelper'
import ShowSlot from './Dialog/showEvent'
import moment, { Moment } from 'moment-timezone'
import Desktop from '@/modules/desktopMobile/desktop'
import { useTranslation } from 'react-i18next'
import { PlanningKPIData, usePlanningKPI } from './hooks/usePlanningKPI'
import { usePlanningDialogs } from './hooks/usePlanningDialogs'
import { Parent } from '@/types/Parent'
import { EventCalendar } from '@/types/Calendar'
import { ChildWithParents } from '@/types/Child'
import { CalendarView } from './utils/calendarParameters'
import ChildWithoutSection from './Dialog/childWithoutSection/childWithoutSection'
import { StaffWithLinks } from '@/types/Staff'
import { DateRateSlot } from '@/types/StaffRate'
import NoChildsDialog from '../account/childs/parentChilds/Dialog/noChilds'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import AddMultipleRegularSlotsDialog from './Dialog/regularSlot/addMultipleRegularSlot/addMultipleRegularSlotsDialog'
import ExportSectionSlotsDialog from '../drafting/list/Dialog/exportSectionSlotsDialog.tsx'
import { Section } from '@/types/Section.ts'
import { PlanningRights, PlanningTabsState } from '@/hooks/usePlanningRights.ts'
import PlanningHeader from './header/header.tsx'
import Calendar from './calendar/calendar.tsx'
import { useParentChildDialogs } from './hooks/useParentChildDialogs.ts'
import { usePlanningFilters } from './hooks/usePlanningFilters.ts'
import { FetchPlanningDataProps } from './hooks/usePlanningSlots.ts'
import { useDialog } from '@/hooks/useDialog/useDialog.tsx'
import OccasionalSlotForm, {
    OccasionalSlotFormProps
} from './Dialog/occasionalSlot/occasionalSlotForm/occasionalSlotForm.tsx'
import { DAY_FORMAT } from './Dialog/occasionalSlot/models/constants.ts'

interface PlanningProps {
    planningTabs: PlanningTabsState
    planningRights: PlanningRights
    fetchData: (props: FetchPlanningDataProps) => void
    parent?: Parent
    calendarEvents: EventCalendar[]
    currentDaycare: Daycare
    refetchData: (isGlobalPlanning?: boolean) => void
    closingDays: Array<ClosingDay>
    works: Array<Work>
    places: Array<OccasionalSlotsPlaces>
    childs: Array<ChildWithParents>
    staffsListData: Array<StaffWithLinks>
    lastFromDate: Moment
    lastToDate: Moment
    requestNumber: number
    isMobile: boolean
    rateSlots: DateRateSlot[]
}

const Planning: FunctionComponent<PlanningProps> = ({
    planningTabs,
    planningRights,
    fetchData,
    parent,
    calendarEvents,
    currentDaycare,
    refetchData,
    closingDays,
    staffsListData,
    works,
    places,
    childs,
    lastFromDate,
    lastToDate,
    requestNumber,
    isMobile,
    rateSlots
}: PlanningProps) => {
    const { t } = useTranslation()

    const navigate: NavigateFunction = useNavigate()

    const isParent: boolean = planningRights.isParent

    const [currentView, setCurrentView] = useState<string>(isMobile ? CalendarView.Agenda : CalendarView.Current)
    const [isFullScreen, setFullScreen] = useState<boolean>(false)

    // The selected date lives here, before `usePlanningDialogs`, which receives `openOccasionalDialog`:
    // the form does not depend on `dialogs`, so it can be built first and opened from the calendar handlers
    const [occasionalSelectedDate, setOccasionalSelectedDate] = useState<Moment>(moment().startOf('day'))

    const { dialog: occasionalSlotFormDialog, openDialog: openOccasionalSlotFormDialog } = useDialog({
        Component: OccasionalSlotForm,
        props: (onClose: () => void): OccasionalSlotFormProps => ({
            type: 'add',
            handleClose: onClose,
            refetchData,
            childs,
            calendarEvents,
            places,
            selectedDate: occasionalSelectedDate.format(DAY_FORMAT)
        }),
        dialogTitle: t('addOccasionalSlot'),
        hideDialogClose: true
    })

    // Both updates are batched by React: the form opens on the date that was just chosen
    function openOccasionalDialog(date: Moment): void {
        setOccasionalSelectedDate(moment(date).startOf('day'))
        openOccasionalSlotFormDialog()
    }

    const {
        dialogs,
        closeRegularDialog,
        closeStaffDialog,
        closeShowSlotDialog,
        openExportDialog,
        closeExportDialog,
        handleSelectWeb,
        handleSelectMobile,
        handleAddSlot,
        handleEventClickArg,
        handleEventClick
    } = usePlanningDialogs({
        currentDaycare,
        lastFromDate,
        activeTabId: planningTabs.activeTabId,
        openOccasionalDialog
    })

    const { isChildWithoutSectionDialogOpen, isNoChildDialogOpen, closeChildWithoutSection, closeNoChild } =
        useParentChildDialogs({ parent, currentDaycare, isParent })

    const {
        selectFilters,
        setSelectFilters,
        selectFiltersOptions,
        sortedStaffs,
        hiddenDays,
        filteredCalendarEvents,
        filterEvents
    } = usePlanningFilters({
        t,
        planningTabs,
        planningRights,
        calendarEvents,
        staffsListData,
        currentDaycare,
        places,
        rateSlots,
        parent
    })

    const kpi: PlanningKPIData = usePlanningKPI({
        calendarEvents,
        lastFromDate,
        lastToDate,
        isParent,
        merger: currentDaycare.merger
    })

    return (
        <div className={styles.PagePosition}>
            {!isFullScreen && (
                <Desktop>
                    <div className={styles.IndicatorsContainer}>
                        <PlanningKPI
                            planningTabs={planningTabs}
                            isParent={isParent}
                            kpi={kpi}
                            filterEvents={filterEvents}
                            merger={currentDaycare.merger}
                        />
                    </div>
                </Desktop>
            )}
            <div
                className={`${styles.CalendarContainer} ${isFullScreen ? styles.FullScreen : ''} ${planningTabs.isStaffTab ? styles.CalendarStaffsContainer : ''}`}
            >
                <div className={styles.Header}>
                    <PlanningHeader
                        exportCalendar={openExportDialog}
                        currentDaycare={currentDaycare}
                        planningRights={planningRights}
                        planningTabs={planningTabs}
                        currentView={currentView}
                        setCurrentView={setCurrentView}
                        refetchData={refetchData}
                        handleAddSlot={handleAddSlot}
                        selectFilters={selectFilters}
                        filterEvents={filterEvents}
                        selectFiltersOptions={selectFiltersOptions}
                        requestNumber={requestNumber}
                        handeSetFullScreen={() => setFullScreen(!isFullScreen)}
                        kpi={kpi}
                        parent={parent}
                        isFullScreen={isFullScreen}
                        setSelectFilters={setSelectFilters}
                    />
                </div>
                <div className={styles.Calendar}>
                    <Calendar
                        planningTabs={planningTabs}
                        planningRights={planningRights}
                        fetchData={fetchData}
                        currentDaycare={currentDaycare}
                        filteredCalendarEvents={filteredCalendarEvents}
                        refetchData={refetchData}
                        sortedStaffs={sortedStaffs}
                        isMobile={isMobile}
                        currentView={currentView}
                        setCurrentView={setCurrentView}
                        hiddenDays={hiddenDays}
                        handleSelectWeb={handleSelectWeb}
                        handleEventClickArg={handleEventClickArg}
                        handleEventClick={handleEventClick}
                        handleSelectMobile={handleSelectMobile}
                    />
                </div>
            </div>
            {planningRights.canWriteStaffWorks && dialogs.staff.isOpen && (
                <EditOrCreateWorkDialog
                    open={dialogs.staff.isOpen}
                    daycare={currentDaycare}
                    staffsListData={staffsListData}
                    handleClose={closeStaffDialog}
                    closingDays={closingDays}
                    date={dialogs.staff.data.date.format('DD-MM-YYYY')}
                    from={getDaycareOpening(currentDaycare, (dialogs.staff.data.date.day() + 6) % 7) || '08:00'}
                    to={getDaycareClosing(currentDaycare, (dialogs.staff.data.date.day() + 6) % 7) || '17:30'}
                    works={works}
                    dateReadonly={false}
                    refetch={() => refetchData(planningTabs.isGlobalTab)}
                />
            )}
            {occasionalSlotFormDialog}
            {planningRights.canWriteContracts && dialogs.regular.isOpen && (
                <AddMultipleRegularSlotsDialog
                    open={dialogs.regular.isOpen}
                    handleClose={closeRegularDialog}
                    childs={childs}
                    currentDaycare={currentDaycare}
                    refetchRegularSlots={() => refetchData(planningTabs.isGlobalTab)}
                    calendarEvents={calendarEvents}
                    startDateString={dialogs.regular.data.start.format('DD-MM-YYYY')}
                    endDateString={dialogs.regular.data.end.format('DD-MM-YYYY')}
                    places={places}
                />
            )}
            {dialogs.showSlot.isOpen && (
                <ShowSlot
                    open={dialogs.showSlot.isOpen}
                    handleClose={closeShowSlotDialog}
                    selectedEvent={dialogs.showSlot.data.event}
                    daycare={currentDaycare}
                    refetchData={() => refetchData(planningTabs.isGlobalTab)}
                    childs={childs}
                    calendarEvents={calendarEvents}
                    places={places}
                    isParent={isParent}
                    parent={parent}
                    works={works}
                    staffsListData={staffsListData}
                    canEditRegular={planningRights.canWriteContracts}
                    canEditStaffWorks={planningRights.canWriteStaffWorks}
                    lastRegularSlotDate={moment().add(1, 'year').format('DD-MM-YYYY')}
                />
            )}
            <ChildWithoutSection open={isChildWithoutSectionDialogOpen} handleClose={closeChildWithoutSection} />
            <NoChildsDialog
                open={isNoChildDialogOpen}
                handleClose={closeNoChild}
                addChild={() => navigate('/account/childs')}
            />
            {dialogs.exportCalendar && (
                <ExportSectionSlotsDialog
                    open={dialogs.exportCalendar}
                    handleClose={closeExportDialog}
                    minDate={
                        currentDaycare.created
                            ? moment(currentDaycare.created, 'DD-MM-YYYY').format('DD-MM-YYYY')
                            : moment().format('DD-MM-YYYY')
                    }
                    daycare={currentDaycare}
                    startDate={lastFromDate.format('DD-MM-YYYY')}
                    endDate={lastToDate.format('DD-MM-YYYY')}
                    preSelectedSections={getSelectedSections(selectFilters, currentDaycare)}
                    isRpeOrLaep={planningRights.isRPEOrLAEP}
                />
            )}
        </div>
    )
}

export default Planning

function getSelectedSections(selectFilters: string[], currentDaycare: Daycare): string[] {
    const filters: string[] = selectFilters
        .filter((filter: string): boolean => filter.startsWith('SECTION_'))
        .map((filter: string): string => filter.replace('SECTION_', ''))

    return filters.length > 0
        ? filters
        : currentDaycare.sections
          ? currentDaycare.sections.map((s: Section): string => s.id.toString())
          : []
}
